import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
// W6: mobile.css wurde bisher von den gelöschten /m-Komponenten importiert;
// die TicketDetail-Mobilregeln + .mobile-container braucht die App weiterhin.
import './styles/mobile.css';
import Home from './components/Home';
import Dashboard from './components/Dashboard';
import PoolMonitor from './components/PoolMonitor';
import MailView from './components/MailView';
import TeamsView from './components/TeamsView';
import CalendarView from './components/CalendarView';
import TicketView from './components/TicketView';
import GlobalCalendarView from './components/GlobalCalendarView';
import ProjectsView from './components/ProjectsView';
import GlobalSearchView from './components/GlobalSearchView';
import Setup from './components/Setup';
import AdminPanel from './components/AdminPanel';
import CustomersPage from './components/CustomersPage';
import Header from './components/Header';
import TicketDetail from './components/TicketDetail';
import useIsMobile from './hooks/useIsMobile';
import useMobilModus from './hooks/useMobilModus';
import MobilStart from './components/mobil/MobilStart';
// Modul-Seiteneffekt: fängt beforeinstallprompt ab, bevor React steht.
import InstallHinweis from './components/mobil/InstallHinweis';
import TerminAbsageRueckfrage from './components/TerminAbsageRueckfrage'; // #554
import EinstellungenAbgleich from './components/EinstellungenAbgleich'; // #564
import { AssistentProvider } from './components/assistent/AssistentProvider'; // #512
import AssistentKnopf from './components/assistent/AssistentKnopf'; // #512
import apiService from './services/apiService';
import { I18nProvider, useI18n } from './utils/i18n';
import { ToastProvider, useToast } from './components/ToastProvider';
// #256: registriert den Mitschnitt der Service-Worker-Störungen beim App-Start,
// damit der Feedback-Knopf sie später mitschicken kann (Modul-Seiteneffekt).
import './utils/swDiagnostics';
import { ConfirmProvider } from './components/ui/ConfirmDialog';

// W6 (#16 A2.6): Der alte /m-Zweig samt MobileGate ist aufgelöst. Seit der
// neuen Mobilansicht (components/mobil/) gehört /m wieder ihr: Handys landen
// dort automatisch, alle übrigen Routen bleiben responsiv.
// - ResumePathTracker übernimmt die frühere MobileGate-Nebenaufgabe, bei jedem
//   Routenwechsel den Pfad für den Auto-Relogin zu sichern (apiService lauscht
//   auf __captureResumePath).
// - LegacyMobileTicketRedirect hält alte /m/ticket/<id>-Deep-Links (z. B. aus
//   Browser-Lesezeichen) am Leben, jetzt mit Ziel Mobilansicht.
const ResumePathTracker = () => {
  const location = useLocation();
  useEffect(() => {
    try { window.dispatchEvent(new CustomEvent('__captureResumePath')); } catch (_) {}
  }, [location.pathname, location.search, location.hash]);
  return null;
};

const LegacyMobileTicketRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/m?t=${encodeURIComponent(id)}`} replace />;
};

const istMobilPfad = (pfad) => pfad === '/m' || String(pfad || '').startsWith('/m/');

// Die Mobilansicht hat ihren eigenen Kopf, der große Header bleibt dort weg.
const KopfZeile = () => {
  const location = useLocation();
  if (istMobilPfad(location.pathname)) return null;
  return <Header />;
};

// In der Mobilansicht öffnet sich ein Ticket von außen (Push-Benachrichtigung,
// openTicketDetailById) in der Handy-Detailansicht statt im Desktop-Dialog.
const MobilTicketUmleitung = ({ ticketId, onErledigt }) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/m?t=${encodeURIComponent(ticketId)}`);
    onErledigt();
  }, [ticketId, navigate, onErledigt]);
  return null;
};

// #229: Hinweis auf Cache-/Offline-Antworten des Service Workers nur einmal pro Seitenlauf
let swDegradedNotified = false;

function AppContent() {
  const [setupRequired, setSetupRequired] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();
  // Global TicketDetail modal state
  const [globalTicket, setGlobalTicket] = useState(null);
  const [showGlobalTicketDetail, setShowGlobalTicketDetail] = useState(false);
  const [, setLoadingGlobalTicket] = useState(false);
  const [globalScrollTarget, setGlobalScrollTarget] = useState(null);
  const isMobile = useIsMobile();
  const mobil = useMobilModus();
  const { pushToast } = useToast();
  // W6 (#16 B4): Offline-Banner — die SW-Fallbacks liefern ggf. Cache-Stände
  const [isOffline, setIsOffline] = useState(() => {
    try { return typeof navigator !== 'undefined' && navigator.onLine === false; } catch (_) { return false; }
  });

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // #322: Kommentare, die im Funkloch abgeschickt wurden, liegen in der
  // Warteschlange. Abgesendet wird beim Wiederverbinden und einmal beim Start
  // -- letzteres, weil das `online`-Ereignis ausbleibt, wenn der Tab
  // zwischendurch geschlossen war.
  useEffect(() => {
    let abgemeldet = false;
    const sende = async () => {
      try {
        const ergebnis = await apiService.sendeOfflineWarteschlange();
        if (abgemeldet) return;
        if (ergebnis.gesendet > 0) {
          pushToast({
            message: ergebnis.gesendet === 1
              ? t('app.offlineQueueSentOne', 'Ihr offline geschriebener Kommentar wurde nachgesendet.')
              : t('app.offlineQueueSentMany', '{{count}} offline geschriebene Kommentare wurden nachgesendet.').replace('{{count}}', String(ergebnis.gesendet)),
            type: 'success'
          });
        }
        if (ergebnis.verworfen.length > 0) {
          pushToast({
            message: t('app.offlineQueueDropped', 'Ein offline geschriebener Kommentar konnte nicht nachgesendet werden und wurde verworfen.'),
            type: 'error',
            timeoutMs: 0
          });
        }
      } catch (_) {}
    };
    sende();
    window.addEventListener('online', sende);
    return () => { abgemeldet = true; window.removeEventListener('online', sende); };
  }, [pushToast, t]);

  // W6 (#16 B5): neuer Service Worker wartet — Toast statt hartem Austausch
  // unter den Füßen des Nutzers; Bestätigung schickt SKIP_WAITING, der
  // controllerchange-Listener in index.js lädt danach genau einmal neu.
  useEffect(() => {
    const onSwUpdate = (e) => {
      const worker = e?.detail?.worker;
      if (!worker) return;
      pushToast({
        message: t('app.updateAvailable', 'Eine neue Version der Anwendung ist verfügbar.'),
        actionLabel: t('app.reloadNow', 'Jetzt aktualisieren'),
        onAction: () => { try { worker.postMessage({ type: 'SKIP_WAITING' }); } catch (_) {} },
        type: 'info',
        timeoutMs: 0
      });
    };
    window.addEventListener('swUpdateAvailable', onSwUpdate);
    return () => window.removeEventListener('swUpdateAvailable', onSwUpdate);
  }, [pushToast, t]);

  // Kehrwoche B (#236 / #31): apiService meldet eine abgelaufene Sitzung per CustomEvent
  // statt blockierendem alert(). Hier: Fehler-Toast mit „Jetzt anmelden"; wer nicht klickt,
  // wird nach 10 s automatisch weitergeleitet (vorher: Redirect direkt nach dem alert).
  useEffect(() => {
    const onSessionExpired = (e) => {
      e.preventDefault(); // signalisiert dem apiService: Toast übernimmt, kein Sofort-Redirect
      const href = e?.detail?.href || '/';
      const go = () => { if (href !== '/' || window.location.pathname !== '/') window.location.href = href; };
      const timer = setTimeout(go, 10000);
      pushToast({
        message: e?.detail?.mode === 'sso'
          ? t('app.sessionExpiredSso', 'Ihre Sitzung ist abgelaufen. Sie werden zur Anmeldung weitergeleitet.')
          : t('app.sessionExpiredManual', 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'),
        actionLabel: t('app.loginNow', 'Jetzt anmelden'),
        onAction: () => { clearTimeout(timer); go(); },
        type: 'error',
        timeoutMs: 0
      });
    };
    window.addEventListener('sessionExpired', onSessionExpired);
    return () => window.removeEventListener('sessionExpired', onSessionExpired);
  }, [pushToast, t]);

  // #229: Der Service Worker liefert beim Browser-Kaltstart Cache-Stände (sw-cached-at)
  // oder 503 {error:'offline'}, obwohl navigator.onLine schon true ist — bisher stumm
  // (alter Kalender, leere Teams-/Mail-Spalten). Jetzt: ein Toast (einmal pro Seitenlauf)
  // mit „Jetzt aktualisieren"; in den ersten 60 s nach dem Laden zusätzlich eine Netz-Sonde
  // (HEAD läuft am SW vorbei), die genau einmal neu lädt, sobald der Server erreichbar ist.
  useEffect(() => {
    let probeTimer = null;
    let probes = 0;
    const stopProbe = () => { if (probeTimer) { clearInterval(probeTimer); probeTimer = null; } };
    const probe = async () => {
      probes += 1;
      try {
        const res = await fetch('/manifest.webmanifest', { method: 'HEAD', cache: 'no-store' });
        if (res && res.ok) {
          stopProbe();
          try { window.location.reload(); } catch (_) {}
          return;
        }
      } catch (_) {}
      if (probes >= 30) stopProbe();
    };
    const onDegraded = (e) => {
      if (swDegradedNotified) return;
      swDegradedNotified = true;
      const cachedAt = e?.detail?.cachedAt ? new Date(e.detail.cachedAt) : null;
      const stamp = cachedAt && !Number.isNaN(cachedAt.getTime()) ? cachedAt.toLocaleString() : null;
      pushToast({
        message: stamp
          ? t('app.degradedStale', 'Keine Verbindung zum Server — angezeigt wird der Stand von {time}.').replace('{time}', stamp)
          : t('app.degradedOffline', 'Keine Verbindung zum Server — einige Bereiche konnten nicht geladen werden.'),
        actionLabel: t('app.reloadNow', 'Jetzt aktualisieren'),
        onAction: () => { try { window.location.reload(); } catch (_) {} },
        type: 'info',
        timeoutMs: 0
      });
      const coldStart = (typeof performance !== 'undefined' && performance.now() < 60000);
      if (coldStart) probeTimer = setInterval(probe, 4000);
    };
    window.addEventListener('swDegradedResponse', onDegraded);
    return () => { window.removeEventListener('swDegradedResponse', onDegraded); stopProbe(); };
  }, [pushToast, t]);

  // Frühe Wiederherstellung des gespeicherten Pfads (falls Auto-Relogin stattfand)
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const APPLY_KEY = 'lastVisitedPathShouldApply';
      const PATH_KEY = 'lastVisitedPathAfterAuth';
      const shouldApply = sessionStorage.getItem(APPLY_KEY) === '1';
      if (!shouldApply) return;
      const path = sessionStorage.getItem(PATH_KEY);
      // Keys sofort verbrauchen, um Loops zu vermeiden
      sessionStorage.removeItem(APPLY_KEY);
      sessionStorage.removeItem(PATH_KEY);
      const current = window.location.pathname + window.location.search + window.location.hash;
      if (path && path !== '/' && !path.startsWith('/auth/') && path !== current) {
        window.location.replace(path);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  // Global listeners to open TicketDetail by id or by ticket object
  useEffect(() => {
    const openById = async (e) => {
      try {
        const detail = e?.detail || {};
        const ticketId = detail.ticketId || detail.id || (typeof detail === 'number' ? detail : null);
        const scrollTo = detail.scrollTo || null;
        if (!ticketId) return;
        setLoadingGlobalTicket(true);
        // Mark as viewed (optimistisch) noch vor dem Laden
        try { await apiService.markTicketAsViewed(ticketId); } catch (_) {}
        const resp = await apiService.getTicket(ticketId);
        const ticket = resp?.ticket || resp;
        if (ticket && ticket.id != null) {
          setGlobalTicket(ticket);
          setShowGlobalTicketDetail(true);
          setGlobalScrollTarget(scrollTo);
        }
      } catch (_) {
      } finally {
        setLoadingGlobalTicket(false);
      }
    };
    const openByObject = (e) => {
      try {
        const detail = e?.detail || {};
        const ticket = detail.ticket || null;
        const scrollTo = detail.scrollTo || null;
        if (!ticket) return;
        setGlobalTicket(ticket);
        setShowGlobalTicketDetail(true);
        setGlobalScrollTarget(scrollTo);
      } catch (_) {}
    };

    window.addEventListener('openTicketDetailById', openById);
    window.addEventListener('openTicketDetail', openByObject);

    // #142: Klick auf eine Desktop-Benachrichtigung — der Service Worker meldet
    // sich per postMessage (bestehendes Fenster) bzw. öffnet ein neues Fenster
    // mit ?openTicket=<id>; beides landet hier im openById-Pfad.
    const onSwMessage = (e) => {
      try {
        if (e?.data?.type === 'openTicket' && e.data.ticketId) {
          openById({ detail: { ticketId: e.data.ticketId } });
        }
      } catch (_) {}
    };
    try { navigator.serviceWorker?.addEventListener('message', onSwMessage); } catch (_) {}
    try {
      const params = new URLSearchParams(window.location.search);
      const openTicketParam = params.get('openTicket');
      if (openTicketParam) {
        // Param entfernen, damit ein Reload das Modal nicht erneut öffnet
        params.delete('openTicket');
        const rest = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
        openById({ detail: { ticketId: Number(openTicketParam) || openTicketParam } });
      }
    } catch (_) {}

    return () => {
      window.removeEventListener('openTicketDetailById', openById);
      window.removeEventListener('openTicketDetail', openByObject);
      try { navigator.serviceWorker?.removeEventListener('message', onSwMessage); } catch (_) {}
    };
  }, []);

  const schliesseGlobalesTicket = useCallback(() => {
    setShowGlobalTicketDetail(false);
    setGlobalTicket(null);
    setGlobalScrollTarget(null);
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await apiService.getHealth();
      setSetupRequired(!response.configured);
    } catch (error) {
      console.error('Error checking setup status:', error);
      setSetupRequired(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>{t('app.loading', 'Lade Anwendung...')}</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {/* #512: Zustand des Assistenten oberhalb der Routen, damit Blase und
          laufende Antwort jeden Ansichtswechsel überstehen */}
      <AssistentProvider>
      <div className="App">
        {isOffline && (
          <div className="app-offline-banner">
            {t('app.offline', 'Offline — angezeigte Daten können veraltet sein.')}
          </div>
        )}
        {!setupRequired && <KopfZeile />}
        <main>
          <ResumePathTracker />
          <Routes>
            {setupRequired ? (
              <>
                <Route path="/setup" element={<Setup />} />
                <Route path="*" element={<Navigate to="/setup" replace />} />
              </>
            ) : (
              <>
                <Route path="/" element={<Home />} />
                {/* Mobilansicht für Handys (Heute / Meine / Pool, Ticket über ?t=<id>) */}
                <Route path="/m" element={<MobilStart />} />
                <Route path="/m/ticket/:id" element={<LegacyMobileTicketRedirect />} />
                <Route path="/m/tickets" element={<Navigate to="/m" replace />} />
                {/* #16: Pool-Monitor — offene Pool-Tickets als große Karten (Überwachungsmonitor) */}
                <Route path="/pool" element={<PoolMonitor />} />
                {/* Handys landen automatisch in der Mobilansicht, außer jemand hat dort "Desktop-Ansicht" gewählt */}
                <Route path="/dashboard" element={mobil.aktiv ? <Navigate to="/m" replace /> : <Dashboard />} />
                <Route path="/mail" element={<MailView />} />
                <Route path="/teams" element={<TeamsView />} />
                <Route path="/calendar" element={<CalendarView />} />
                <Route path="/tickets" element={<TicketView />} />
                <Route path="/tickets/:id" element={<TicketView />} />
                <Route path="/global-calendar" element={<GlobalCalendarView />} />
                <Route path="/projects" element={<ProjectsView />} />
                <Route path="/search" element={<GlobalSearchView />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/setup" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
          {/* Global Ticket Detail Modal (opened from anywhere via events) */}
          {showGlobalTicketDetail && globalTicket && mobil.aktiv && (
            <MobilTicketUmleitung ticketId={globalTicket.id} onErledigt={schliesseGlobalesTicket} />
          )}
          {showGlobalTicketDetail && globalTicket && !mobil.aktiv && (
            isMobile ? (
              // Mobile Vollbild-Ansicht nutzt dieselbe TicketDetail-Komponente mit mobile Styling
              <div className="mobile-container">
                <div className="mobile-content">
                  <TicketDetail
                    ticket={globalTicket}
                    scrollTo={globalScrollTarget}
                    onClose={(hasChanges = false) => {
                      setShowGlobalTicketDetail(false);
                      setGlobalTicket(null);
                      setGlobalScrollTarget(null);
                      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'globalTicketDetailClosed', hasChanges } })); } catch (_) {}
                    }}
                    onTicketUpdate={() => {
                      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'globalTicketDetailUpdated' } })); } catch (_) {}
                    }}
                    isMobileView={true}
                  />
                </div>
              </div>
            ) : (
              <TicketDetail
                ticket={globalTicket}
                scrollTo={globalScrollTarget}
                onClose={(hasChanges = false) => {
                  setShowGlobalTicketDetail(false);
                  setGlobalTicket(null);
                  setGlobalScrollTarget(null);
                  try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'globalTicketDetailClosed', hasChanges } })); } catch (_) {}
                }}
                onTicketUpdate={() => {
                  try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'globalTicketDetailUpdated' } })); } catch (_) {}
                }}
              />
            )
          )}
        </main>
        {!setupRequired && <InstallHinweis />}
        <TerminAbsageRueckfrage />
        {!setupRequired && <EinstellungenAbgleich />}
        {!setupRequired && <AssistentKnopf />}
      </div>
      </AssistentProvider>
    </Router>
  );
}

function App() {
  return (
    <I18nProvider>
      <ToastProvider>
        <ConfirmProvider>
          <AppContent />
        </ConfirmProvider>
      </ToastProvider>
    </I18nProvider>
  );
}

export default App;